import { inject, Injectable } from "@angular/core";
import { environment } from "../../../../environments/environment";
import { HttpClient, HttpResponse } from "@angular/common/http";
import { BehaviorSubject, combineLatest, map, Observable, tap, } from "rxjs";
import { ControlerEnum } from "../../../shared/enums/controler.enum";
import { IBussinessSubjectAlreadyUsed, IBussinessSubjectAlreadyUsedResult, ICreateVendor, ICreateVendorResult, IDeleteVendor, IGetVendor, IUpdateVendor, IVendorCountryResult, IVendorList, IVendorManageData, IVendorPagination } from "../interfaces/vendor.interface";
import { VendorManageEnum } from "../enums/vendor-manage.enum";

@Injectable({
  providedIn: 'root'
})
export class VendorService {
  apiBaseUrl: string = environment.apiBaseUrl;
  private http = inject(HttpClient);

  activeVendorGuid: string | null = null;

  private changedActiveVendorSubject$ = new BehaviorSubject<string | null>(null);
  readonly changedActiveVendor$ = this.changedActiveVendorSubject$.asObservable().pipe(
    tap(guid => this.activeVendorGuid = guid)
  );

  private manageVendorSubject$ = new BehaviorSubject<IVendorManageData | null>(null);
  readonly manageVendor$ = this.manageVendorSubject$.asObservable();

  getVendors(data: IVendorPagination): Observable<HttpResponse<IVendorList>> {
    let query = `?pageSize=${data.pageSize}&pageNumber=${data.pageNumber}`;

    if (data.countryName) {
      query += `&countryName=${data.countryName}`;
    }

    if (data.name) {
      query += `&name=${data.name}`;
    }

    if (data.shortName) {
      query += `&shortName=${data.shortName}`;
    }

    return this.http.get<IVendorList>(
      `${this.apiBaseUrl}/${ControlerEnum.Vendor}${query}`,
      { observe: 'response' },
    );
  }

  getVendorById(vendorGuid: string): Observable<HttpResponse<IGetVendor>> {
    return this.http.get<IGetVendor>(
      `${this.apiBaseUrl}/${ControlerEnum.Vendor}/${vendorGuid}`,
      { observe: 'response' },
    );
  }

  getVendorsByCountry(countryId: number) {
    return this.http.get<IVendorCountryResult>(
      `${this.apiBaseUrl}/${ControlerEnum.Vendorcountry}/${countryId}`,
      { observe: 'response' },
    );
  }

  getVendorsByUser() {
    return this.http.get<IVendorCountryResult>(
      `${this.apiBaseUrl}/${ControlerEnum.VendorUser}`,
      { observe: 'response' },
    );
  }

  getHeaderVendorsByCountry(countryId: number) {
    return combineLatest([
      this.getVendorsByCountry(countryId),
      this.manageVendor$
    ]).pipe(
      map(([response, manage]) => {
        if (manage && manage.action !== VendorManageEnum.noAction) {
          switch (manage.action) {
            case VendorManageEnum.created: {
              if (response.body && manage.vendor) {
                response.body.result.push(manage.vendor);
              }
              break;
            }
            case VendorManageEnum.updated: {
              const updatedVendor = response.body?.result.find(v => v.guid === manage.vendorGuid);

              if (updatedVendor && manage.vendor) {
                const index = response.body!.result.findIndex(v => v.guid === manage.vendorGuid);

                if (index !== -1) {
                  response.body!.result[index] = manage.vendor;
                }
              }
              break;
            }
            case VendorManageEnum.deleted: {
              const vendor = response.body?.result.find(v => v.guid === manage.vendorGuid);

              if (vendor) {
                response.body!.result = response.body!.result.filter(v => v.guid !== manage.vendorGuid);
              }
              break;
            }
          }
          
          manage.action = VendorManageEnum.noAction;
        }
        return response;
      })
    );
  }

  createVendor(
    model: ICreateVendor,
  ): Observable<HttpResponse<ICreateVendorResult>> {
    const createModel: ICreateVendor = {
      businessSubjectVATNo: model.businessSubjectVATNo,
      countryId: model.countryId,
      name: model.name,
      shortname: model.shortname,
      users: model.users,
      businessSubjectIds: model.businessSubjectIds
    };

    return this.http.post<ICreateVendorResult>(
      `${this.apiBaseUrl}/${ControlerEnum.Vendor}`,
      createModel,
      { observe: 'response' },
    );
  }

  updateVendor(
    model: IUpdateVendor,
  ): Observable<HttpResponse<ICreateVendorResult>> {
    const updateModel: IUpdateVendor = {
      guid: model.guid,
      businessSubjectVATNo: model.businessSubjectVATNo,
      countryId: model.countryId,
      name: model.name,
      shortname: model.shortname,
      users: model.users,
      businessSubjectIds: model.businessSubjectIds
    };

    return this.http.put<ICreateVendorResult>(
      `${this.apiBaseUrl}/${ControlerEnum.Vendor}`,
      updateModel,
      { observe: 'response' },
    );
  }

  isBussinessSubjectAlreadyUsed(model: IBussinessSubjectAlreadyUsed): Observable<HttpResponse<IBussinessSubjectAlreadyUsedResult>> {
    return this.http.post<IBussinessSubjectAlreadyUsedResult>(
      `${this.apiBaseUrl}/${ControlerEnum.Vendor}/isBussinessSubjectAlreadyUsed`,
      model,
      { observe: 'response' },
    );
  }

  deleteVendor(
    vendorId: string,
    createdBy: string,
  ): Observable<HttpResponse<IDeleteVendor>> {
    const model = {
      createdBy: createdBy,
    };
    return this.http.delete<IDeleteVendor>(
      `${this.apiBaseUrl}/${ControlerEnum.Vendor}/${vendorId}`,
      { observe: 'response', body: model },
    );
  }

  changedActiveVendor(value: string | null): void {
    this.changedActiveVendorSubject$.next(value);
  }

  manageVendor(data: IVendorManageData): void {
    this.manageVendorSubject$.next(data);
  }
}